import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { ArrowLeft, ChevronLeft, ChevronRight, Search } from 'lucide-react'
import { adminApi, type AdminUser } from '@/api/admin'
import { Card } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { PageHeader } from '@/components/PageHeader'
import { copy } from '@/lib/copy'
import { getApiErrorMessage } from '@/lib/apiError'
import { cn } from '@/components/ui/cn'
import { useAuthStore } from '@/store/authStore'

const USERS_QUERY_KEY = ['admin-users'] as const

function TierBadge({ tier }: { tier: AdminUser['tier'] }) {
  return (
    <span
      className={cn(
        'rounded-full px-2 py-0.5 text-xs font-medium',
        tier === 'ADMIN' && 'bg-violet-500/10 text-violet-600 dark:text-violet-400',
        tier === 'PRO' && 'bg-amber-500/10 text-amber-700 dark:text-amber-400',
        tier === 'FREE' && 'bg-muted text-muted-foreground',
      )}
    >
      {tier}
    </span>
  )
}

function UserRow({ user }: { user: AdminUser }) {
  const queryClient = useQueryClient()
  const currentUserId = useAuthStore((state) => state.user?.id)
  const isSelf = currentUserId === user.id

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: USERS_QUERY_KEY })
  }

  const roleMutation = useMutation({
    mutationFn: (role: 'USER' | 'ADMIN') => adminApi.updateUserRole(user.id, role),
    onSuccess: invalidate,
  })

  const premiumMutation = useMutation({
    mutationFn: (enabled: boolean) => adminApi.updateUserPremium(user.id, enabled),
    onSuccess: invalidate,
  })

  const handleRoleToggle = () => {
    const nextRole = user.role === 'ADMIN' ? 'USER' : 'ADMIN'
    const message =
      nextRole === 'ADMIN'
        ? copy.admin.usersPromoteConfirm.replace('{email}', user.email)
        : copy.admin.usersDemoteConfirm.replace('{email}', user.email)
    if (!window.confirm(message)) return
    roleMutation.mutate(nextRole)
  }

  const handlePremiumToggle = () => {
    const grant = user.premiumOverride !== true
    const message = grant
      ? copy.admin.usersGrantPremiumConfirm.replace('{email}', user.email)
      : copy.admin.usersRevokePremiumConfirm.replace('{email}', user.email)
    if (!window.confirm(message)) return
    premiumMutation.mutate(grant)
  }

  const isPending = roleMutation.isPending || premiumMutation.isPending
  const error = roleMutation.error ?? premiumMutation.error

  return (
    <Card className="p-4">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate font-medium text-foreground">{user.email}</p>
            {user.role === 'ADMIN' ? (
              <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                Admin
              </span>
            ) : null}
            <TierBadge tier={user.tier} />
            {user.premiumOverride === true ? (
              <span className="rounded-full bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-400">
                {copy.admin.usersPremiumGranted}
              </span>
            ) : null}
          </div>
          {user.displayName ? (
            <p className="mt-1 text-sm text-muted-foreground">{user.displayName}</p>
          ) : null}
          <p className="mt-1 text-xs text-muted-foreground/70">
            {copy.admin.usersJoined} {new Date(user.createdAt).toLocaleDateString()}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={isPending || (isSelf && user.role === 'ADMIN')}
            onClick={handleRoleToggle}
            className={cn(
              'inline-flex h-9 items-center rounded-md px-3 text-xs font-medium transition-colors disabled:opacity-50',
              user.role === 'ADMIN'
                ? 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                : 'bg-primary text-primary-foreground hover:bg-primary/90',
            )}
          >
            {user.role === 'ADMIN' ? copy.admin.usersDemoteAdmin : copy.admin.usersMakeAdmin}
          </button>

          {user.role !== 'ADMIN' ? (
            <button
              type="button"
              disabled={isPending}
              onClick={handlePremiumToggle}
              className={cn(
                'inline-flex h-9 items-center rounded-md border px-3 text-xs font-medium transition-colors disabled:opacity-50',
                user.premiumOverride === true
                  ? 'border-border bg-background text-foreground hover:bg-muted'
                  : 'border-amber-500/30 bg-amber-500/10 text-amber-800 hover:bg-amber-500/20 dark:text-amber-300',
              )}
            >
              {user.premiumOverride === true
                ? copy.admin.usersRevokePremium
                : copy.admin.usersGrantPremium}
            </button>
          ) : null}
        </div>
      </div>

      {error ? (
        <p className="mt-2 text-xs text-destructive">
          {getApiErrorMessage(error, copy.admin.usersUpdateFailed)}
        </p>
      ) : null}
    </Card>
  )
}

export function AdminUsersPage() {
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)

  const usersQuery = useQuery({
    queryKey: [...USERS_QUERY_KEY, search, page],
    queryFn: () => adminApi.listUsers({ search: search || undefined, page, size: 20 }),
  })

  const totalPages = usersQuery.data?.totalPages ?? 0

  const runSearch = () => {
    setPage(0)
    setSearch(searchInput.trim())
  }

  return (
    <div data-testid="admin-users">
      <PageHeader title={copy.admin.usersTitle} description={copy.admin.usersDescription} />

      <div className="mb-4">
        <Link
          to="/admin"
          className="inline-flex h-9 items-center gap-1 rounded-md bg-secondary px-3 text-xs text-secondary-foreground hover:bg-secondary/80"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          {copy.admin.backToAdmin}
        </Link>
      </div>

      <Card className="mb-6 p-4">
        <h2 className="text-sm font-semibold">{copy.admin.usersHowItWorksTitle}</h2>
        <ul className="mt-2 list-disc space-y-1 pl-4 text-sm text-muted-foreground">
          {copy.admin.usersHowItWorks.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </Card>

      <div className="mb-4 flex gap-2">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onEnter={runSearch}
            placeholder={copy.admin.usersSearchPlaceholder}
            className="pl-9"
          />
        </div>
        <button
          type="button"
          onClick={runSearch}
          className="inline-flex h-10 shrink-0 items-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          {copy.admin.usersSearch}
        </button>
      </div>

      {usersQuery.isLoading ? (
        <p className="text-sm text-muted-foreground">{copy.admin.usersLoading}</p>
      ) : usersQuery.isError ? (
        <p className="text-sm text-destructive">
          {getApiErrorMessage(usersQuery.error, copy.admin.usersLoadFailed)}
        </p>
      ) : (
        <>
          <div className="space-y-3">
            {(usersQuery.data?.content ?? []).map((user) => (
              <UserRow key={user.id} user={user} />
            ))}
            {(usersQuery.data?.content.length ?? 0) === 0 ? (
              <p className="text-sm text-muted-foreground">{copy.admin.usersEmpty}</p>
            ) : null}
          </div>

          {totalPages > 1 ? (
            <div className="mt-6 flex items-center justify-center gap-3">
              <button
                type="button"
                disabled={page === 0}
                onClick={() => setPage((p) => p - 1)}
                className="inline-flex h-9 items-center gap-1 rounded-md border px-3 text-xs disabled:opacity-50"
              >
                <ChevronLeft className="h-4 w-4" />
                {copy.admin.usersPrev}
              </button>
              <span className="text-sm text-muted-foreground">
                {page + 1} / {totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages - 1}
                onClick={() => setPage((p) => p + 1)}
                className="inline-flex h-9 items-center gap-1 rounded-md border px-3 text-xs disabled:opacity-50"
              >
                {copy.admin.usersNext}
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          ) : null}
        </>
      )}
    </div>
  )
}
